import React, { useEffect, useState, useCallback } from 'react'
import '../../assets/theme/theme.less'
import './commissions.css'
import { LayoutComponent as Layout, TitleTabs as Title } from '../../components'
import { Row, Col, Tabs, message as alert } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { 
    getCommissions, 
    getCommissionDetails, 
    approveCommission, 
    rejectCommission,
    generateCommissions,
    resetSuccessAction,
    resetErrorMessage
} from '../../redux/slices/commissionsSlice'

import CommissionsTable from './commissionsTable'
import CommissionDetailsModal from './commissionDetailsModal'
import RejectCommissionModal from './rejectCommissionModal'
import GenerateCommissionsModal from './generateCommissionsModal'

const { TabPane } = Tabs

const Commissions = () => {
    const dispatch = useDispatch()
    
    // State variables
    const [detailsModalVisible, setDetailsModalVisible] = useState(false)
    const [rejectModalVisible, setRejectModalVisible] = useState(false)
    const [generateModalVisible, setGenerateModalVisible] = useState(false)
    const [selectedCommissionId, setSelectedCommissionId] = useState(null)
    const [currentStatus, setCurrentStatus] = useState('pending')
    
    // Redux state
    const { 
        isLoading, 
        commissions, 
        commission,
        message, 
        isSubmit, 
        successAction
    } = useSelector(state => state.commissions)
    
    // Fetch commissions with specific status
    const fetchCommissions = useCallback((status) => {
        setCurrentStatus(status)
        dispatch(getCommissions({ status }))
    }, [dispatch])
    
    // Initial data fetch
    useEffect(() => {
        fetchCommissions('pending')
    }, [fetchCommissions])
    
    // Handle error messages separately
    useEffect(() => {
        if (message) {
            alert.error(message)
            
            // Reset error message after displaying it
            setTimeout(() => {
                dispatch(resetErrorMessage())
            }, 3000)
        }
    }, [message, dispatch])
    
    // Handle success actions separately
    useEffect(() => {
        if (successAction) {
            fetchCommissions(currentStatus)
            setDetailsModalVisible(false)
            setRejectModalVisible(false)
            setGenerateModalVisible(false)
            alert.success('Operação realizada com sucesso!')
            dispatch(resetSuccessAction())
        }
    }, [successAction, currentStatus, dispatch, fetchCommissions])
    
    // Handle tab change
    const handleTabChange = (key) => {
        let status
        switch (key) {
            case '1':
                status = 'pending'
                break
            case '2':
                status = 'approved'
                break
            case '3':
                status = 'rejected'
                break
            default:
                status = 'pending'
        }
        fetchCommissions(status)
    }
    
    // Details modal handlers
    const showDetailsModal = (id) => {
        setSelectedCommissionId(id)
        dispatch(getCommissionDetails(id))
        setDetailsModalVisible(true)
    }
    
    const handleDetailsModalOk = () => {
        setDetailsModalVisible(false)
    }
    
    const handleDetailsModalCancel = () => {
        setDetailsModalVisible(false)
    }
    
    // Approve commission
    const handleApprove = (id) => {
        dispatch(approveCommission(id))
    }
    
    // Reject modal handlers
    const showRejectModal = (id) => {
        setSelectedCommissionId(id)
        setRejectModalVisible(true)
    }
    
    const handleRejectModalCancel = () => {
        setRejectModalVisible(false)
    }
    
    const handleReject = (id, rejectionReason) => {
        dispatch(rejectCommission({ id, rejectionReason }))
    }
    
    const handleGenerateModalCancel = () => {
        setGenerateModalVisible(false)
    }
    
    const handleGenerate = (contractId) => {
        dispatch(generateCommissions(contractId))
    }
    
    // Determine notification count for each tab
    const pendingCount = commissions.filter(c => c.status === 'pending').length
    const approvedCount = commissions.filter(c => c.status === 'approved').length
    const rejectedCount = commissions.filter(c => c.status === 'rejected').length
    
    return (
        <Layout width>
            <Row>
                <Col md={18}>
                    <h1 className="title page-title">Comissões</h1>
                </Col>
                {/* <Col md={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={showGenerateModal}
                    >
                        Gerar Comissões
                    </Button>
                </Col> */}
            </Row>
            <Row className="mt-15" gutter={[15,15]}>
                <Col span={24}>
                    <Tabs defaultActiveKey="1" centered onChange={handleTabChange}>
                        <TabPane tab={<Title title="Pendentes" notification={pendingCount} />} key="1">
                            <Row className="mt-15">
                                <Col span={24} id="commissions-table">
                                    <CommissionsTable 
                                        data={commissions} 
                                        loading={isLoading}
                                        showDetailsModal={showDetailsModal}
                                        handleApprove={handleApprove}
                                        handleReject={showRejectModal}
                                        refreshList={() => fetchCommissions(currentStatus)}
                                    />
                                </Col>
                            </Row>
                        </TabPane>
                        <TabPane tab={<Title title="Aprovadas" notification={approvedCount} />} key="2">
                            <Row className="mt-15">
                                <Col span={24} id="commissions-table">
                                    <CommissionsTable 
                                        data={commissions} 
                                        loading={isLoading}
                                        showDetailsModal={showDetailsModal}
                                        handleApprove={handleApprove}
                                        handleReject={showRejectModal}
                                        refreshList={() => fetchCommissions(currentStatus)}
                                    />
                                </Col>
                            </Row>
                        </TabPane>
                        <TabPane tab={<Title title="Rejeitadas" notification={rejectedCount} />} key="3">
                            <Row className="mt-15">
                                <Col span={24} id="commissions-table">
                                    <CommissionsTable 
                                        data={commissions} 
                                        loading={isLoading}
                                        showDetailsModal={showDetailsModal}
                                        handleApprove={handleApprove}
                                        handleReject={showRejectModal}
                                        refreshList={() => fetchCommissions(currentStatus)}
                                    />
                                </Col>
                            </Row>
                        </TabPane>
                    </Tabs>
                </Col>
            </Row>
            
            {/* Modals */}
            <CommissionDetailsModal 
                visible={detailsModalVisible}
                commission={commission}
                loading={isLoading}
                handleOk={handleDetailsModalOk}
                handleCancel={handleDetailsModalCancel}
                handleApprove={handleApprove}
                handleReject={showRejectModal}
                isSubmit={isSubmit}
            />
            
            <RejectCommissionModal 
                visible={rejectModalVisible}
                handleCancel={handleRejectModalCancel}
                handleReject={handleReject}
                commissionId={selectedCommissionId}
                isSubmit={isSubmit}
            />
            
            <GenerateCommissionsModal 
                visible={generateModalVisible}
                handleCancel={handleGenerateModalCancel}
                handleGenerate={handleGenerate}
                isSubmit={isSubmit}
            />
        </Layout>
    )
}

export default Commissions